// The code editor: a plain <textarea> with transparent text, a highlighted
// <pre> rendered behind it with identical font metrics, a line-number gutter
// with diagnostic markers, current-line highlight, and a few editing
// niceties (Tab indent, list continuation, Ctrl/Cmd+S). Scrolling is owned
// by the textarea; the overlay and gutter follow via transforms.
import { highlightLines, marksFromDiagnostics } from '../../src/lib/highlight.ts';
import { h, clear } from './dom.js';

const LIST_MARKER = /^(\s*)([-*+]\s+)(\(\s?\)\s+|\([xX]\)\s+|\[\s?\]\s+|\[[xX]\]\s+)?(.*)$/;
const HELP_LINE = /^(\s*)(>\s?)(.*)$/;

/**
 * @param {HTMLElement} container
 * @param {{ value?: string, onChange?: (value: string) => void, onSave?: () => void, onCursor?: (line: number, column: number) => void }} options
 */
export function createEditor(container, options = {}) {
  const gutter = h('div.ed-gutter', { 'aria-hidden': 'true' });
  const gutterInner = h('div.ed-gutter-inner');
  gutter.append(gutterInner);
  const layer = h('div.ed-layer', { 'aria-hidden': 'true' });
  const curLine = h('div.ed-curline');
  const pre = h('pre.ed-hl');
  const code = h('code');
  pre.append(code);
  layer.append(curLine, pre);
  const textarea = h('textarea.ed-input', {
    spellcheck: 'false',
    autocapitalize: 'off',
    autocomplete: 'off',
    autocorrect: 'off',
    wrap: 'off',
    'aria-label': 'Askdown source',
    'aria-multiline': 'true',
  });
  const status = h('div.ed-status', { 'aria-hidden': 'true' });
  const root = h('div.ed', gutter, layer, textarea);
  container.append(root, status);

  let value = '';
  let diagnostics = [];
  let marks = new Map();
  let lineCount = 1;
  let currentLine = 1;
  let lineHeight = 22;
  let padTop = 12;
  let suppressChange = false;

  function measure() {
    const cs = getComputedStyle(textarea);
    lineHeight = parseFloat(cs.lineHeight) || 22;
    padTop = parseFloat(cs.paddingTop) || 0;
  }

  function renderHighlight() {
    const lines = highlightLines(value, marks);
    lineCount = lines.length;
    // A trailing newline still needs a visible last line for height parity.
    code.innerHTML = lines.join('\n') + '\n';
    renderGutter();
    updateCurrentLine();
  }

  let gutterLines = 0;
  function renderGutter() {
    const diagByLine = new Map();
    for (const d of diagnostics) {
      const prev = diagByLine.get(d.line);
      if (!prev || d.severity === 'error') diagByLine.set(d.line, d);
    }
    if (gutterLines !== lineCount) {
      clear(gutterInner);
      const frag = document.createDocumentFragment();
      for (let i = 1; i <= lineCount; i++) {
        frag.append(h('div.ed-ln', { dataset: { line: String(i) } }, String(i)));
      }
      gutterInner.append(frag);
      gutterLines = lineCount;
      gutter.style.width = `${Math.max(3, String(lineCount).length + 1.5)}ch`;
      textarea.style.paddingLeft = pre.style.paddingLeft = layer.style.paddingLeft = '';
      updateGutterOffset();
    }
    const children = gutterInner.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      const d = diagByLine.get(i + 1);
      const cls = d ? `ed-ln ed-ln--${d.severity}` : 'ed-ln';
      if (el.className !== cls) el.className = cls;
      if (d) el.title = d.message;
      else if (el.title) el.removeAttribute('title');
    }
  }

  function updateGutterOffset() {
    const w = gutter.getBoundingClientRect().width;
    root.style.setProperty('--ed-gutter-w', `${w}px`);
  }

  function updateCurrentLine() {
    const pos = textarea.selectionStart;
    let line = 1;
    for (let i = 0; i < pos && i < value.length; i++) if (value.charCodeAt(i) === 10) line++;
    currentLine = line;
    curLine.style.transform = `translateY(${padTop + (line - 1) * lineHeight}px)`;
    curLine.style.height = `${lineHeight}px`;
    for (const el of gutterInner.querySelectorAll('.ed-ln--current')) el.classList.remove('ed-ln--current');
    const ln = gutterInner.children[line - 1];
    if (ln) ln.classList.add('ed-ln--current');
    const lineStart = value.lastIndexOf('\n', pos - 1) + 1;
    const column = pos - lineStart + 1;
    status.textContent = `Ln ${line}, Col ${column}`;
    if (options.onCursor) options.onCursor(line, column);
  }

  function syncScroll() {
    const x = textarea.scrollLeft;
    const y = textarea.scrollTop;
    layer.style.transform = `translate(${-x}px, ${-y}px)`;
    gutterInner.style.transform = `translateY(${-y}px)`;
  }

  function emitChange() {
    if (suppressChange) return;
    if (options.onChange) options.onChange(value);
  }

  function readValue() {
    value = textarea.value;
    renderHighlight();
    emitChange();
  }

  // --- Editing helpers ---------------------------------------------------

  /** Replaces the selection with text, keeping the native undo stack when possible. */
  function insertText(text) {
    textarea.focus();
    let done = false;
    try {
      done = document.execCommand && document.execCommand('insertText', false, text);
    } catch {
      done = false;
    }
    if (!done) {
      const { selectionStart, selectionEnd } = textarea;
      textarea.setRangeText(text, selectionStart, selectionEnd, 'end');
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function lineRange(start, end) {
    const from = value.lastIndexOf('\n', start - 1) + 1;
    let to = value.indexOf('\n', end === start ? end : end - 1);
    if (to === -1) to = value.length;
    return [from, to];
  }

  function indentSelection(outdent) {
    const { selectionStart, selectionEnd } = textarea;
    const [from, to] = lineRange(selectionStart, selectionEnd);
    const block = value.slice(from, to);
    const lines = block.split('\n');
    let firstDelta = 0;
    const changed = lines.map((l, i) => {
      if (outdent) {
        const m = /^( {1,2}|\t)/.exec(l);
        const removed = m ? m[0].length : 0;
        if (i === 0) firstDelta = -removed;
        return l.slice(removed);
      }
      if (i === 0) firstDelta = 2;
      return '  ' + l;
    });
    const replacement = changed.join('\n');
    textarea.setSelectionRange(from, to);
    insertText(replacement);
    const delta = replacement.length - block.length;
    const newStart = Math.max(from, selectionStart + firstDelta);
    const newEnd = Math.max(newStart, selectionEnd + delta);
    if (selectionStart === selectionEnd) textarea.setSelectionRange(newStart, newStart);
    else textarea.setSelectionRange(from, to + delta);
  }

  function onKeyDown(e) {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.shiftKey && !e.altKey && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      if (options.onSave) options.onSave();
      return;
    }
    if (e.key === 'Tab' && !mod && !e.altKey) {
      e.preventDefault();
      const { selectionStart, selectionEnd } = textarea;
      const multiline = value.slice(selectionStart, selectionEnd).includes('\n');
      if (e.shiftKey || multiline) indentSelection(e.shiftKey);
      else insertText('  ');
      return;
    }
    if (e.key === 'Enter' && !mod && !e.shiftKey && !e.altKey && !e.isComposing) {
      const { selectionStart, selectionEnd } = textarea;
      if (selectionStart !== selectionEnd) return;
      const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
      let lineEnd = value.indexOf('\n', selectionStart);
      if (lineEnd === -1) lineEnd = value.length;
      const line = value.slice(lineStart, lineEnd);
      const atEnd = selectionStart === lineEnd;
      let m = LIST_MARKER.exec(line);
      if (m) {
        const [, indent, bullet, marker, rest] = m;
        if (rest.trim() === '' && atEnd) {
          // Empty item: end the list by clearing the marker.
          e.preventDefault();
          textarea.setSelectionRange(lineStart, lineEnd);
          insertText(indent);
          return;
        }
        e.preventDefault();
        const nextMarker = marker ? marker.replace(/[xX]/, ' ') : '';
        insertText(`\n${indent}${bullet}${nextMarker}`);
        return;
      }
      m = HELP_LINE.exec(line);
      if (m && atEnd) {
        const [, indent, marker, rest] = m;
        if (rest.trim() === '') {
          e.preventDefault();
          textarea.setSelectionRange(lineStart, lineEnd);
          insertText(indent);
          return;
        }
        e.preventDefault();
        insertText(`\n${indent}${marker}`);
        return;
      }
      // Keep indentation.
      const indent = /^\s*/.exec(line)[0];
      if (indent && atEnd) {
        e.preventDefault();
        insertText(`\n${indent}`);
      }
    }
  }

  textarea.addEventListener('input', readValue);
  textarea.addEventListener('scroll', syncScroll, { passive: true });
  textarea.addEventListener('keydown', onKeyDown);
  const cursorEvents = ['keyup', 'click', 'select', 'focus'];
  for (const ev of cursorEvents) textarea.addEventListener(ev, updateCurrentLine);
  document.addEventListener('selectionchange', () => {
    if (document.activeElement === textarea) updateCurrentLine();
  });
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(() => {
      measure();
      updateGutterOffset();
      updateCurrentLine();
    }).observe(root);
  }

  // --- Public API ----------------------------------------------------------

  const api = {
    el: root,
    textarea,
    getValue: () => value,
    /** Sets the text without firing onChange. Keeps the scroll position and caret by default. */
    setValue(next, { keepScroll = true, silent = true } = {}) {
      if (next === value) return;
      const { scrollTop, scrollLeft, selectionStart, selectionEnd } = textarea;
      suppressChange = silent;
      textarea.value = next;
      value = next;
      renderHighlight();
      if (keepScroll) {
        textarea.scrollTop = scrollTop;
        textarea.scrollLeft = scrollLeft;
        const len = next.length;
        textarea.setSelectionRange(Math.min(selectionStart, len), Math.min(selectionEnd, len));
      } else {
        textarea.scrollTop = 0;
        textarea.scrollLeft = 0;
        textarea.setSelectionRange(0, 0);
      }
      syncScroll();
      updateCurrentLine();
      suppressChange = false;
      if (!silent) emitChange();
    },
    setDiagnostics(list) {
      diagnostics = list || [];
      marks = marksFromDiagnostics(diagnostics);
      renderHighlight();
    },
    focus: () => textarea.focus({ preventScroll: true }),
    /** Scrolls a line into view and places the caret there. */
    gotoLine(line, { focus = true } = {}) {
      const lines = value.split('\n');
      const target = Math.max(1, Math.min(line, lines.length));
      let pos = 0;
      for (let i = 0; i < target - 1; i++) pos += lines[i].length + 1;
      const top = padTop + (target - 1) * lineHeight;
      const viewH = textarea.clientHeight;
      if (top < textarea.scrollTop + lineHeight || top > textarea.scrollTop + viewH - lineHeight * 2) {
        textarea.scrollTop = Math.max(0, top - viewH / 2);
      }
      if (focus) {
        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(pos, pos);
      }
      syncScroll();
      updateCurrentLine();
      flashLine(target);
    },
    currentLine: () => currentLine,
    lineCount: () => lineCount,
    measure,
  };

  function flashLine(line) {
    const el = gutterInner.children[line - 1];
    curLine.classList.remove('ed-curline--flash');
    void curLine.offsetWidth;
    if (el) curLine.classList.add('ed-curline--flash');
  }

  measure();
  api.setValue(options.value || '', { keepScroll: false });
  requestAnimationFrame(() => {
    measure();
    updateGutterOffset();
    updateCurrentLine();
  });
  return api;
}
